import {
  composeSystemId,
  type SystemGroup,
  type TopologyEdge,
  type TopologyGraph,
} from '@internal/topology-viewer';

/**
 * `outage`   = reached through an unbroken chain of critical dependencies.
 * `degraded` = reached through a non-critical dependency; stays up but impaired.
 * `at-risk`  = reached through a dependency whose criticality was never declared.
 */
export type ImpactSeverity = 'outage' | 'degraded' | 'at-risk';

const SEVERITY_RANK: Record<ImpactSeverity, number> = {
  'at-risk': 1,
  degraded: 2,
  outage: 3,
};

export interface ImpactedSystem {
  /** Composed system id, matching `composeSystemId(domain, key)`. */
  id: string;
  key: string;
  /** Catalog namespace of the System entity; `key` + this addresses the entity page. */
  namespace: string;
  displayName: string;
  domain: string;
  region?: string;
  owner: string;
  team: string;
  severity: ImpactSeverity;
  /** Hops from the nearest seed; seeds are 0. */
  depth: number;
  /** Ids of the immediate upstream systems responsible for this severity. */
  via: string[];
  customers: string[];
}

export interface ImpactedCustomer {
  customer: string;
  /** Worst case across every impacted system serving this customer. */
  severity: ImpactSeverity;
  systems: Array<{ id: string; displayName: string; severity: ImpactSeverity }>;
}

export interface BlastRadius {
  /** Seeds that actually resolved to an in-scope system. */
  seeds: string[];
  impactedSystems: ImpactedSystem[];
  impactedCustomers: ImpactedCustomer[];
}

export interface BlastRadiusOptions {
  /** Restricts the radius to systems in this region plus region-less (global) ones. */
  region?: string;
}

interface SystemEntry {
  id: string;
  system: SystemGroup;
}

interface VisitState {
  severity: ImpactSeverity;
  depth: number;
  via: Set<string>;
}

/**
 * Edge endpoints are mostly composed ids, but system-level `dependsOn` targets
 * are recorded bare. Bare names are only resolved when unambiguous.
 */
function buildIndex(graph: TopologyGraph) {
  const byId = new Map<string, SystemEntry>();
  const byBareKey = new Map<string, string[]>();

  for (const domain of graph.domains) {
    for (const system of domain.systems) {
      const id = composeSystemId(system.domain, system.key);
      if (!byId.has(id)) byId.set(id, { id, system });
      const bare = byBareKey.get(system.key) ?? [];
      if (!bare.includes(id)) bare.push(id);
      byBareKey.set(system.key, bare);
    }
  }

  const resolve = (ref: string): string | undefined => {
    if (byId.has(ref)) return ref;
    const candidates = byBareKey.get(ref);
    return candidates?.length === 1 ? candidates[0] : undefined;
  };

  return { byId, resolve };
}

function gradeEdge(
  edge: TopologyEdge,
): { severity: ImpactSeverity; cascades: boolean } | undefined {
  if (edge.relation === 'hasPart') return undefined;
  switch (edge.dependencyType) {
    case 'critical':
      return { severity: 'outage', cascades: true };
    case 'noncritical':
      return { severity: 'degraded', cascades: false };
    // `optional` and any unrecognised/missing type: the dependent keeps running,
    // so it is flagged but never propagates.
    default:
      return { severity: 'at-risk', cascades: false };
  }
}

/** Region-less systems are treated as global, so they survive every region filter. */
function inScope(system: SystemGroup, region?: string): boolean {
  if (!region) return true;
  return !system.region || system.region === region;
}

/**
 * Walks the dependency graph backwards from systems assumed to be down and
 * reports which systems cascade and which customers they serve.
 *
 * Only systems at `outage` propagate further — a degraded system does not take
 * its own dependents down. Severity can only be upgraded, never lowered, which
 * is what makes cyclic dependency graphs terminate.
 */
export function computeBlastRadius(
  graph: TopologyGraph,
  seedKeys: string[],
  opts: BlastRadiusOptions = {},
): BlastRadius {
  const { region } = opts;
  const { byId, resolve } = buildIndex(graph);

  const dependentsOf = new Map<string, Array<{ sourceId: string; edge: TopologyEdge }>>();
  for (const edge of graph.edges) {
    const sourceId = resolve(edge.sourceSystem);
    const targetId = resolve(edge.targetSystem);
    if (!sourceId || !targetId || sourceId === targetId) continue;
    const list = dependentsOf.get(targetId) ?? [];
    list.push({ sourceId, edge });
    dependentsOf.set(targetId, list);
  }

  const state = new Map<string, VisitState>();
  const queue: string[] = [];
  const seeds: string[] = [];

  for (const seedKey of seedKeys) {
    const id = resolve(seedKey);
    if (!id || state.has(id)) continue;
    const entry = byId.get(id)!;
    if (!inScope(entry.system, region)) continue;
    state.set(id, { severity: 'outage', depth: 0, via: new Set() });
    queue.push(id);
    seeds.push(id);
  }

  while (queue.length) {
    const currentId = queue.shift()!;
    const current = state.get(currentId)!;
    for (const { sourceId, edge } of dependentsOf.get(currentId) ?? []) {
      const grade = gradeEdge(edge);
      if (!grade) continue;
      const dependent = byId.get(sourceId);
      if (!dependent || !inScope(dependent.system, region)) continue;

      const depth = current.depth + 1;
      const existing = state.get(sourceId);
      if (!existing) {
        state.set(sourceId, { severity: grade.severity, depth, via: new Set([currentId]) });
        if (grade.cascades) queue.push(sourceId);
        continue;
      }
      if (SEVERITY_RANK[grade.severity] > SEVERITY_RANK[existing.severity]) {
        existing.severity = grade.severity;
        existing.depth = Math.min(existing.depth, depth);
        existing.via = new Set([currentId]);
        if (grade.cascades) queue.push(sourceId);
      } else if (grade.severity === existing.severity) {
        existing.via.add(currentId);
        existing.depth = Math.min(existing.depth, depth);
      }
    }
  }

  const impactedSystems: ImpactedSystem[] = Array.from(state.entries())
    .map(([id, visit]) => {
      const { system } = byId.get(id)!;
      return {
        id,
        key: system.key,
        namespace: system.namespace || 'default',
        displayName: system.displayName,
        domain: system.domain,
        region: system.region,
        owner: system.owner,
        team: system.team,
        severity: visit.severity,
        depth: visit.depth,
        via: Array.from(visit.via).sort(),
        customers: Array.from(new Set(system.customers ?? [])).sort(),
      };
    })
    .sort(
      (a, b) =>
        SEVERITY_RANK[b.severity] - SEVERITY_RANK[a.severity] ||
        a.depth - b.depth ||
        a.displayName.localeCompare(b.displayName),
    );

  const customerMap = new Map<string, ImpactedCustomer>();
  for (const system of impactedSystems) {
    for (const customer of system.customers) {
      const entry = customerMap.get(customer) ?? { customer, severity: system.severity, systems: [] };
      if (SEVERITY_RANK[system.severity] > SEVERITY_RANK[entry.severity]) {
        entry.severity = system.severity;
      }
      entry.systems.push({ id: system.id, displayName: system.displayName, severity: system.severity });
      customerMap.set(customer, entry);
    }
  }

  const impactedCustomers = Array.from(customerMap.values()).sort(
    (a, b) =>
      SEVERITY_RANK[b.severity] - SEVERITY_RANK[a.severity] ||
      b.systems.length - a.systems.length ||
      a.customer.localeCompare(b.customer),
  );

  return { seeds, impactedSystems, impactedCustomers };
}

/** Node-id keyed severities for tinting the topology graph. */
export function toSeverityMap(radius: BlastRadius | null): Map<string, ImpactSeverity> {
  const map = new Map<string, ImpactSeverity>();
  for (const system of radius?.impactedSystems ?? []) {
    map.set(system.id, system.severity);
  }
  return map;
}

export const SEVERITY_COLORS: Record<ImpactSeverity, string> = {
  outage: '#ff4d4d',
  degraded: '#ffb366',
  'at-risk': '#64748b',
};

export const SEVERITY_LABELS: Record<ImpactSeverity, string> = {
  outage: 'Outage',
  degraded: 'Degraded',
  'at-risk': 'At risk',
};
